import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart, cartTotal, clearCart, tableStore, removeItemsByMenuId } from "@/lib/cart";
import { orderToken } from "@/lib/orderToken";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { formatMoney } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Wallet, CreditCard, Ticket, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";

export default function Checkout() {
  const cart = useCart();
  const auth = useAuth();
  const navigate = useNavigate();
  const tbl = tableStore.get();

  const [payment, setPayment] = useState("counter");
  const [coupon, setCoupon] = useState("");
  const [redeem, setRedeem] = useState(0);
  const [quote, setQuote] = useState(null);
  const [quoting, setQuoting] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Authoritative price preview from the backend — the summary renders THIS, never
  // its own math, so the number shown always equals the number charged.
  const itemsKey = JSON.stringify(cart.map((i) => [i.menu_item_id, i.quantity]));
  useEffect(() => {
    if (cart.length === 0) { setQuote(null); return; }
    let cancelled = false;
    setQuoting(true);
    const t = setTimeout(() => {
      api.post("/orders/quote", {
        items: cart.map((i) => ({ menu_item_id: i.menu_item_id, quantity: i.quantity, selected_option_ids: i.selected_option_ids || [] })),
        table_token: tbl?.token || null,
        coupon_code: coupon || null,
        loyalty_points_redeem: redeem || 0,
      })
        .then((r) => { if (!cancelled) setQuote(r.data); })
        .catch(() => { if (!cancelled) setQuote(null); })
        .finally(() => { if (!cancelled) setQuoting(false); });
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [itemsKey, coupon, redeem]);

  const currency = quote?.currency || "INR";
  const money = (v) => formatMoney(v, currency);
  // Pay Online requires a UPI id AND the online-payment flag on (explicit false = off).
  const upiOn = !!tbl?.restaurant?.upi_id && tbl?.restaurant?.online_payment_enabled !== false;
  const unavailableIds = quote?.unavailable_item_ids || [];
  const unavailableNames = cart.filter((i) => unavailableIds.includes(i.menu_item_id)).map((i) => i.name);
  const total = quote ? quote.total : cartTotal();
  const minRedeem = quote?.min_redeem ?? 50;
  const walletBalance = quote?.wallet_balance ?? 0;  // scoped to this restaurant by the quote

  const submit = async () => {
    if (!tbl) return toast.error("No table detected");
    if (cart.length === 0) return toast.error("Cart is empty");
    setSubmitting(true);
    try {
      const r = await api.post("/orders", {
        table_token: tbl.token,
        items: cart.map((i) => ({ menu_item_id: i.menu_item_id, quantity: i.quantity, notes: i.notes || null, selected_option_ids: i.selected_option_ids || [] })),
        coupon_code: coupon || null,
        payment_method: payment,
        loyalty_points_redeem: redeem || 0,
      });
      orderToken.save(r.data.id, r.data.access_token);  // capability for viewing this order later
      clearCart();
      toast.success("Order placed!");
      navigate(`/order/${r.data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Order failed");
    } finally { setSubmitting(false); }
  };

  if (!tbl) return (
    <div className="min-h-screen bg-[var(--qd-bg)] qd-grain">
      <header className="p-5"><Link to="/" className="text-sm font-semibold text-[var(--qd-primary)]">← QRDine home</Link></header>
      <div className="grid place-items-center p-8 text-center min-h-[60vh]">
        <div className="max-w-sm">
          <div className="text-4xl mb-3" aria-hidden>🍽️</div>
          <p className="font-display text-xl font-bold mb-1">Start by scanning your table</p>
          <p className="text-sm text-[var(--qd-muted)] mb-6">Scan the QR code on your table to browse the menu and place an order.</p>
          <Link to="/"><Button className="h-12 rounded-full bg-[var(--qd-primary)] hover:bg-[var(--qd-primary-dark)]">Go to QRDine home</Button></Link>
        </div>
      </div>
    </div>
  );
  if (cart.length === 0) return (
    <div className="min-h-screen grid place-items-center p-8">
      <div className="text-center">
        <p className="text-[var(--qd-muted)] mb-4">Your cart is empty</p>
        <Link to={`/m/${tbl.token}`}><Button className="rounded-full bg-[var(--qd-primary)]">Browse Menu</Button></Link>
      </div>
    </div>
  );

  const couponFeedback = coupon && quote && (
    quote.coupon_status === "applied"
      ? <div className="text-xs text-green-600 mt-2 inline-flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" aria-hidden /> {quote.coupon_code} applied — {money(quote.discount)} off</div>
      : quote.coupon_status === "invalid"
        ? <div className="text-xs text-red-500 mt-2 inline-flex items-center gap-1"><XCircle className="w-3.5 h-3.5" aria-hidden /> Invalid or expired code</div>
        : quote.coupon_status === "already_used"
          ? <div data-testid="coupon-already-used" className="text-xs text-red-500 mt-2 inline-flex items-center gap-1"><XCircle className="w-3.5 h-3.5" aria-hidden /> You've already used this coupon</div>
          : quote.coupon_status === "signin_required"
            ? <div data-testid="coupon-signin" className="text-xs text-amber-600 mt-2">
                Coupons need an account — <Link to="/login" className="font-semibold underline">sign in to apply</Link> (your cart stays put)
              </div>
            : null
  );

  return (
    <div className="min-h-screen bg-[var(--qd-bg)] qd-grain pb-40">
      <header className="p-5 flex items-center justify-between">
        <button data-testid="checkout-back" aria-label="Back to cart" onClick={() => navigate("/cart")} className="w-11 h-11 rounded-full bg-white grid place-items-center border border-[var(--qd-border)]"><ArrowLeft className="w-4 h-4" aria-hidden /></button>
        <h1 className="font-display font-bold text-xl">Checkout</h1>
        <div className="w-11" />
      </header>

      <div className="px-5 space-y-5">
        {/* Table */}
        <div className="bg-white rounded-2xl p-4 border border-[var(--qd-border)]">
          <div className="text-xs font-mono-alt uppercase tracking-widest text-[var(--qd-muted)]">Serving at</div>
          <div className="font-display font-bold text-lg">{tbl.table?.label} · {tbl.restaurant?.name}</div>
        </div>

        {/* Your order — itemized review */}
        <div className="bg-white rounded-2xl p-4 border border-[var(--qd-border)]">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs font-mono-alt uppercase tracking-widest text-[var(--qd-muted)]">Your order</div>
            <Link to="/cart" data-testid="checkout-edit-cart" className="text-xs text-[var(--qd-primary)] font-semibold">Edit</Link>
          </div>
          <div className="space-y-3">
            {cart.map((it, i) => (
              <div key={i} className="flex gap-3 items-start" data-testid={`checkout-item-${i}`}>
                <span className="text-sm font-bold text-[var(--qd-primary)] shrink-0">{it.quantity}×</span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold">{it.name}</div>
                  {it.modifiers && it.modifiers.length > 0 && <div className="text-xs text-[var(--qd-muted)]">{it.modifiers.map((m) => m.option_name).join(", ")}</div>}
                  {it.notes && <div className="text-xs text-[var(--qd-muted)] italic">“{it.notes}”</div>}
                </div>
                <span className="text-sm font-semibold whitespace-nowrap">{money(it.price * it.quantity)}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Login prompt */}
        {!auth && (
          <Link to="/login" data-testid="checkout-login-cta" className="block bg-[var(--qd-cream)] rounded-2xl p-4 border border-[var(--qd-border)]">
            <div className="text-sm font-semibold">Sign in to earn loyalty points →</div>
            <div className="text-xs text-[var(--qd-muted)] mt-1">Earn points on every order · redeem for discounts</div>
          </Link>
        )}

        {/* Loyalty — balance comes from the quote, scoped to THIS restaurant */}
        {auth && walletBalance >= minRedeem && (
          <div className="bg-white rounded-2xl p-4 border border-[var(--qd-border)]">
            <div className="flex items-center justify-between mb-2">
              <div className="inline-flex items-center gap-2 text-sm font-semibold"><Wallet className="w-4 h-4 text-[var(--qd-primary)]" aria-hidden /> Loyalty Wallet</div>
              <span className="text-xs text-[var(--qd-muted)]">{walletBalance} pts here</span>
            </div>
            <Input data-testid="checkout-loyalty-input" type="number" min="0" max={walletBalance} value={redeem}
              onChange={(e) => setRedeem(Math.max(0, Math.min(walletBalance, parseInt(e.target.value, 10) || 0)))}
              placeholder={`Points to redeem (min ${minRedeem})`} className="rounded-xl" />
            {quote && quote.loyalty_discount > 0 && <div className="text-xs text-[var(--qd-primary)] mt-2">−{money(quote.loyalty_discount)} discount applied</div>}
            {redeem > 0 && quote && quote.loyalty_discount === 0 && <div className="text-xs text-[var(--qd-muted)] mt-2">Minimum {minRedeem} points to redeem</div>}
          </div>
        )}

        {/* Coupon */}
        <div className="bg-white rounded-2xl p-4 border border-[var(--qd-border)]">
          <div className="inline-flex items-center gap-2 text-sm font-semibold mb-2"><Ticket className="w-4 h-4" aria-hidden /> Coupon</div>
          <Input data-testid="checkout-coupon-input" value={coupon} onChange={(e) => setCoupon(e.target.value.toUpperCase())} placeholder="Enter code" className="rounded-xl uppercase" />
          {couponFeedback}
        </div>

        {/* Payment method */}
        <div className="bg-white rounded-2xl p-4 border border-[var(--qd-border)]">
          <div className="text-sm font-semibold mb-3">Payment Method</div>
          <RadioGroup value={payment} onValueChange={setPayment} className="space-y-3">
            <div className="flex items-center gap-3 p-3 rounded-xl border border-[var(--qd-border)]" data-testid="pay-counter-option">
              <RadioGroupItem value="counter" id="counter" />
              <Label htmlFor="counter" className="flex-1 cursor-pointer">
                <div className="font-semibold text-sm">Pay at Counter</div>
                <div className="text-xs text-[var(--qd-muted)]">Cash, UPI or card — after your meal</div>
              </Label>
              <CheckCircle2 className="w-4 h-4 text-[var(--qd-secondary)]" aria-hidden />
            </div>
            {/* Hidden entirely when online payment is off (no upi_id, or flag false) —
                cleaner than a greyed-out row; checkout then shows only Pay at Counter. */}
            {upiOn && (
            <div className="flex items-center gap-3 p-3 rounded-xl border border-[var(--qd-border)]" data-testid="pay-online-option">
              <RadioGroupItem value="online" id="online" />
              <Label htmlFor="online" className="flex-1 cursor-pointer">
                <div className="font-semibold text-sm">Pay Online · UPI</div>
                <div className="text-xs text-[var(--qd-muted)]">Scan or tap to pay now with any UPI app</div>
              </Label>
              <CreditCard className="w-4 h-4 text-[var(--qd-muted)]" aria-hidden />
            </div>
            )}
          </RadioGroup>
        </div>

        {/* Some cart items aren't on this restaurant's menu (item hidden, or a
            stale cart from another place). Offer a one-tap fix. */}
        {unavailableNames.length > 0 && (
          <div data-testid="checkout-unavailable" className="bg-amber-50 border border-amber-200 rounded-2xl p-4 text-sm">
            <div className="font-semibold text-amber-800 mb-1">Not available here</div>
            <div className="text-amber-700 text-xs mb-3">{unavailableNames.join(", ")} {unavailableNames.length === 1 ? "isn't" : "aren't"} on this menu and can't be ordered.</div>
            <Button data-testid="remove-unavailable" onClick={() => removeItemsByMenuId(quote.unavailable_item_ids)} className="h-9 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-xs">Remove {unavailableNames.length === 1 ? "it" : "them"}</Button>
          </div>
        )}

        {/* Summary — rendered from the authoritative backend quote */}
        <div className="bg-white rounded-2xl p-4 border border-[var(--qd-border)] space-y-2 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-xs font-mono-alt uppercase tracking-widest text-[var(--qd-muted)]">Bill</span>
            {quoting && <span className="text-[10px] text-[var(--qd-muted)]">updating…</span>}
          </div>
          <Row label="Subtotal" val={money(quote?.subtotal ?? cartTotal())} />
          {quote?.discount > 0 && <Row label={`Coupon${quote.coupon_code ? ` · ${quote.coupon_code}` : ""}`} val={`−${money(quote.discount)}`} accent />}
          {quote?.loyalty_discount > 0 && <Row label={`Loyalty · ${quote.loyalty_points_redeemed} pts`} val={`−${money(quote.loyalty_discount)}`} accent />}
          {quote?.tax_mode === "exclusive" && <Row label={`GST (${quote.tax_percent}%)`} val={money(quote.tax)} />}
          <div className="border-t border-[var(--qd-border)] pt-2 mt-2 flex justify-between font-display font-bold text-lg">
            <span>Total</span>
            <span data-testid="checkout-total">{money(total)}</span>
          </div>
          {quote?.tax_mode === "inclusive" && quote.tax > 0 && (
            <div className="text-right text-xs text-[var(--qd-muted)]">incl. GST {money(quote.tax)} ({quote.tax_percent}%)</div>
          )}
        </div>
      </div>

      <div className="fixed bottom-0 left-0 right-0 p-5 bg-white/95 backdrop-blur border-t border-[var(--qd-border)]">
        <Button data-testid="place-order-btn" disabled={submitting || unavailableNames.length > 0 || (quoting && !quote)} onClick={submit} className="w-full h-14 rounded-full bg-[var(--qd-primary)] hover:bg-[var(--qd-primary-dark)] text-white font-semibold text-base disabled:opacity-60">
          {submitting ? "Placing order…"
            : (quoting && !quote) ? "Calculating total…"
            : unavailableNames.length > 0 ? "Remove unavailable items to continue"
            : `Place Order · ${money(total)}`}
        </Button>
      </div>
    </div>
  );
}

function Row({ label, val, accent }) {
  return (
    <div className="flex justify-between">
      <span className="text-[var(--qd-muted)]">{label}</span>
      <span className={accent ? "text-[var(--qd-primary)] font-semibold" : ""}>{val}</span>
    </div>
  );
}
